import { Injectable, OnDestroy } from "@angular/core";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import * as moment from "moment";
import { InHouseOrderService } from "../../services/inHouseOrder.service";
import { CoreLogService } from "../../services/log.service";
import { Customer, POSTable_Item, POSTable_ItemStatus, POSTable_Item_Extra, NotificationTyp } from "../../services/IInHouseOrderService";
import { IMenuCourse, ITableOverviewService, ITableSeat, ITableSeatItem, ITablSeatItemExtra, OrderedItemState, ITableSeat_NotificationTyp } from "./inHouseTableOverView.facade";
import Utils from "../../shared/utils";
import { WebShopService } from "../../services/webShop.service";

class TableSeatItemSum implements ITableSeatItem {
  /** id ist bei der summe nicht definiert */
  id: number = NaN;
  /** Name des Items, müsste bei Language change upgedated werden. */
  label: string;
  /** verfügbare anzahl */
  count: number;
  /** Einzelpreis */
  price: number;

  extras: ITablSeatItemExtra[] = [];
  valid_date?: number;
  hasComment?: boolean;
  comment?: string;
  state: OrderedItemState;

  constructor(item: ITableSeatItem) {
    const me = this;
    // id keeps NaN
    me.label = item.label;
    me.price = item.price;
    me.count = item.count;
    me.extras = new Array(...item.extras);
    me.state = item.state;
  }

  /** prüfung ob Item in die Summe aufgenommen werden kann */
  public isFromItem(item: ITableSeatItem) {
    let found = this.label === item.label
      && this.price === item.price
      && this.state === item.state
      && this.extras.length == item.extras.length;
    if (found) {
      for (let index = 0; index < this.extras.length; ++index) {
        const thisExtra = this.extras[index];
        const otherExtra = item.extras[index];
        found = thisExtra.title === otherExtra.title && thisExtra.count === otherExtra.count;
        if (!found) {
          break; // for
        }
      }
    }
    return found;
  }

  /** summiert das item auf das Bestehende */
  public sumItem(item: ITableSeatItem) {
    this.count += item.count;
  }
}

class TablSeatItemExtra implements ITablSeatItemExtra {
  get title(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._model.name_en) ? this._model.name_de : this._model.name_en;
  }
  get subIndex(): number {
    return 0; //! todo interne verknüpfung muss möglichsein
  }
  get count(): number {
    return this._model.amount;
  }

  constructor(
    private _model: POSTable_Item_Extra
  ) { }
}

class TableSeatItem implements ITableSeatItem {
  public get id(): number {
    return this._item.id;
  }
  public get label(): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this._item.name_en) ? this._item.name_de : this._item.name_en;
  }
  public get count(): number {
    return this._item.amount;
  }
  public get price(): number {
    return this._item.price / 100;
  }
  public get valid_date(): number {
    return this._item.validDate_YMD;
  }
  public get extras(): ITablSeatItemExtra[] {
    return this.extraItems;
  }

  public hasComment: boolean = false;
  public get comment(): string {
    return ""; // bestellte artikel haben keine Kommentare momentan
  }
  public set comment(value: string) {
    throw new Error("can't change comment");
  }

  /** auch GroupMemberOrderDetailService beachten */
  public get state(): OrderedItemState {
    switch (this._item.status) {
      case POSTable_ItemStatus.Orderd:
        return OrderedItemState.InPreperation;
      case POSTable_ItemStatus.Preparing:
        return OrderedItemState.InPreperation;
      case POSTable_ItemStatus.PickUpReady:
        const item = this._item;
        // item gilt nach 10 Minuten automatisch als abgeholt
        if (item.readyAt_YMD > 0) {
          const now = moment();
          const auto_pickedup_at = Utils.GetMomentFromInt(item.readyAt_YMD, item.readyAt_HMS).add(10, "minutes");
          if (now > auto_pickedup_at) {
            return OrderedItemState.PickedUp;
          }
        }
        return OrderedItemState.ReadyForPickUp;
      case POSTable_ItemStatus.Complete:
        return OrderedItemState.PickedUp;
      case POSTable_ItemStatus.NoPreparation:
        return OrderedItemState.None; // todo NoPreparation soll auch ein status bekommen
      case POSTable_ItemStatus.TableService:
        return OrderedItemState.None; // todo TableService fehlt noch
      case POSTable_ItemStatus.Unknown:
        return OrderedItemState.None;
      default:
        return OrderedItemState.None;
    }
  }

  public extraItems: TablSeatItemExtra[];

  constructor(private _item: POSTable_Item) {
    const me = this;
    me.hasComment = false;
    me.extraItems = _item.extras.map((extra) => new TablSeatItemExtra(extra));
  }
}

class TableSeat implements ITableSeat {
  id: string | number = "";
  nickname: string = "";
  isGuest: boolean = false;
  seat: number = 0;
  notificationTyp: ITableSeat_NotificationTyp;

  open_amount: number;
  open_price: number;
  items: ITableSeatItem[];

  constructor(_customer: Customer, _items: POSTable_Item[],) {
    if (_customer) {
      this.id = _customer.user_token;
      this.nickname = _customer.nickname;
      this.isGuest = _customer.guest;
      this.seat = _customer.place_no;
      this.notificationTyp = ITableSeat_NotificationTyp.NONE;
      if (_customer.notification_typ === NotificationTyp.WebPush) {
        this.notificationTyp = ITableSeat_NotificationTyp.WebPush;
      } else if (_customer.notification_typ === NotificationTyp.SMS) {
        this.notificationTyp = ITableSeat_NotificationTyp.SMS;
      } else if (_customer.notification_typ === NotificationTyp.FCM) {
        this.notificationTyp = ITableSeat_NotificationTyp.FCM;
      } // else keep NONE
    }
    this.items = _items.map((item) => (new TableSeatItem(item)));
    this.open_price = this.items.reduce((a, b) => a + b.price * b.count, 0);
    this.open_amount = this.items.reduce((a, b) => a + b.count, 0);
  }
}

@Injectable()
export class InHouseTableOrderOverviewService implements ITableOverviewService, OnDestroy {
  private _onTableChangedSubscriber: Subscription;

  constructor(
    private _inHouseOrderService: InHouseOrderService,
    private _webShopService: WebShopService,
    private _coreLogService: CoreLogService,
  ) {
    const me = this;
    // Subscribe to TableChanges
    me._onTableChangedSubscriber = me._inHouseOrderService.ObserveOnTableChanged.subscribe(() => {
      me._bindTableSeats();
    });
    me._bindTableSeats();
  }

  private _tableSeatsSubject: BehaviorSubject<ITableSeat[]> = new BehaviorSubject<ITableSeat[]>(null);
  public get tableSeats$(): Observable<ITableSeat[]> {
    return this._tableSeatsSubject.asObservable();
  }

  /** Menu course */
  private _menucourses: IMenuCourse[] = [];
  private _menucoursesSubject: BehaviorSubject<IMenuCourse[]> = new BehaviorSubject<IMenuCourse[]>([]);
  get menucourses$(): Observable<IMenuCourse[]> {
    return this._menucoursesSubject.asObservable();
  }

  /** use _setPhoneNumber */
  private _phoneNumber: string = "";
  /** aktualisiert die anzahl offener Artikel */
  private _setPhoneNumber(value: string): void {
    const me = this;
    if (me._phoneNumber != value) {
      me._phoneNumber = value;
      me._phoneNumberSubject.next(value);
    }
  }
  private _phoneNumberSubject: BehaviorSubject<string> = new BehaviorSubject<string>("");
  /** user phone number */
  public get phoneNumber$(): Observable<string> {
    return this._phoneNumberSubject.asObservable();
  }

  private async _bindTableSeats() {
    const me = this;
    const table = await me._inHouseOrderService.GetTable(null); // null damit kein unnötiger Abruf stattfindet
    const userToken = me._inHouseOrderService.UserToken;
    const seats = me._inHouseOrderService.GetSeatsFromTable(table);
    try {
      // new TableSeats erzeugen
      const newTableSeats: ITableSeat[] = [];
      let resetPhoneNumber = true;
      for (const key in seats) {
        if (Object.prototype.hasOwnProperty.call(seats, key)) {
          const { customer, pos_items } = seats[key];
          if (pos_items.length > 0) {
            newTableSeats.push(new TableSeat(customer, pos_items));
          }
          // its me, and use SMS notification
          if (customer && customer.user_token === userToken && customer.notification_typ === NotificationTyp.SMS) {
            resetPhoneNumber = false;
            me._inHouseOrderService.GetNotificationInfo().then(info => {
              // bei SMS ist es info die Telefonnummer
              me._setPhoneNumber(info);
            }).catch(error => {
              me._coreLogService.error("InHouseTableOrderOverviewService._bindTableSeats", "GetNotificationInfo catch", error);
            });
          }
        }
      }
      if (resetPhoneNumber) {
        me._setPhoneNumber("");
      }

      newTableSeats.sort((left, right) => {
        if (left.id === userToken || right.id === "") {
          return -1;
        } else if (right.id === userToken || left.id === "") {
          return 1;
        } else if (left.seat < right.seat) {
          return -1;
        } else if (left.seat > right.seat) {
          return 1;
        }
        // else left.seat == right.seat // sollte aber nicht sein
        return 0;
      });
      me._tableSeatsSubject.next(newTableSeats);

    } catch (error) {
      me._coreLogService.error("InHouseTableOrderOverviewService._bindTableSeats", "catch", error);
    }
  }

  public sumItems(items: ReadonlyArray<ITableSeatItem>): ITableSeatItem[] {
    return items.reduce<TableSeatItemSum[]>((acc_items, item) => {
      const acc_item = acc_items.find(sum_item => sum_item.isFromItem(item));
      if (acc_item) {
        acc_item.sumItem(item);
      } else {
        acc_items.push(new TableSeatItemSum(item));
      }
      return acc_items;
    }, []);
  }

  /** in house settings: customer use Eping */
  public async useNotification(): Promise<boolean> {
    const me = this;
    let useEPing = false;
    const settings = await me._webShopService.getWebshopSettingsDataModel();
    if (settings.in_house_order.use_eping_all_locations) {
      useEPing = true;
    } else if (settings.in_house_order.use_eping_locations && settings.in_house_order.use_eping_locations.indexOf(me._inHouseOrderService.TableData.location) >= 0) {
      useEPing = true;
    }
    return useEPing;
  }

  /** ändert die Notification auf SMS mit engegebener Nummer */
  public async ChangePhoneNumber(number: string): Promise<boolean> {
    const me = this;
    return me._inHouseOrderService.setNotification("SMS", number);;
  }

  ngOnDestroy(): void {
    if (this._onTableChangedSubscriber) {
      this._onTableChangedSubscriber.unsubscribe();
    }
  }
}

